import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Users,
  UserCheck,
  UsersRound,
  Activity,
  TrendingUp,
  AlertTriangle,
  Plus,
  RefreshCw,
  Calendar as CalendarIcon,
  TreePine
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Fehler beim Laden der Dashboard-Daten');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats();
  };

  const getStatusColor = (status) => {
    const colors = {
      aktiv: 'text-emerald-600 bg-emerald-50 border-emerald-200',
      inaktiv: 'text-red-600 bg-red-50 border-red-200',
      krank: 'text-amber-600 bg-amber-50 border-amber-200',
      urlaub: 'text-blue-600 bg-blue-50 border-blue-200'
    };
    return colors[status] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      aktiv: '✅',
      inaktiv: '❌',
      krank: '🤒',
      urlaub: '🌴'
    };
    return icons[status] || '❓';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Lade Dashboard-Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 p-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-slate-600 mt-1">Willkommen im SKALARTECH Management System</p>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            className="gap-2"
            data-testid="refresh-dashboard-button"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
        
        <div className="text-sm text-slate-500">
          Letzte Aktualisierung: {new Date().toLocaleString('de-DE')}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="modern-card border-l-4 border-l-emerald-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Gesamte Mitarbeiter</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="total-employees-count">
                  {stats?.total_employees || 0}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-full">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="modern-card border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Aktive Mitarbeiter</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="active-employees-count">
                  {stats?.active_employees || 0}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <UserCheck className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="modern-card border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Teams</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="total-teams-count">
                  {stats?.total_teams || 0}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <UsersRound className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="modern-card border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Heutige Einsätze</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="todays-assignments-count">
                  {stats?.todays_assignments || 0}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <CalendarIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="modern-card border-l-4 border-l-teal-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Offene Urlaubsanträge</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="pending-vacations-count">
                  {stats?.pending_vacations || 0}
                </p>
              </div>
              <div className="p-3 bg-teal-100 rounded-full">
                <TreePine className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="modern-card border-l-4 border-l-orange-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Mitarbeiter im Urlaub</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="employees-on-vacation-count">
                  {stats?.employees_on_vacation || 0}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Users className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employee Status Breakdown */}
      {stats?.employee_status_breakdown && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="modern-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" />
                Mitarbeiter Status Übersicht
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(stats.employee_status_breakdown).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between p-3 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{getStatusIcon(status)}</span>
                      <div>
                        <p className="font-medium text-slate-900 capitalize">{status}</p>
                        <p className="text-sm text-slate-500">{count} Mitarbeiter</p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full border ${getStatusColor(status)}`}>
                      <span className="text-sm font-medium">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="modern-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Schnellaktionen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Button 
                  className="w-full justify-start gap-3 h-12 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                  variant="ghost"
                  onClick={() => window.location.href = '/employees'}
                  data-testid="quick-add-employee-button"
                >
                  <Plus className="w-4 h-4" />
                  Neuen Mitarbeiter hinzufügen
                </Button>
                
                <Button 
                  className="w-full justify-start gap-3 h-12 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                  variant="ghost"
                  onClick={() => window.location.href = '/teams'}
                  data-testid="quick-add-team-button"
                >
                  <UsersRound className="w-4 h-4" />
                  Neues Team erstellen
                </Button>
                
                <Button 
                  className="w-full justify-start gap-3 h-12 bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200"
                  variant="ghost"
                  onClick={() => window.location.href = '/calendar'}
                  data-testid="quick-calendar-button"
                >
                  <CalendarIcon className="w-4 h-4" />
                  Kalender öffnen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* System Notifications */}
      <Card className="modern-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            System Benachrichtigungen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="p-1 bg-emerald-200 rounded-full">
                <span className="text-emerald-600 text-sm">✓</span>
              </div>
              <div>
                <p className="font-medium text-emerald-800">System bereit</p>
                <p className="text-sm text-emerald-600">SKALARTECH Management System ist einsatzbereit.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="p-1 bg-blue-200 rounded-full">
                <span className="text-blue-600 text-sm">ℹ</span>
              </div>
              <div>
                <p className="font-medium text-blue-800">Erste Schritte</p>
                <p className="text-sm text-blue-600">Beginnen Sie mit der Erstellung von Mitarbeitern und Teams.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;